@file:Suppress("MagicNumber")

package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.internal.text.charCountOf
import top.wcpe.allincore.ui.runtime.internal.text.codePointAtCompat
import top.wcpe.allincore.ui.runtime.internal.text.codePointCountCompat
import top.wcpe.allincore.ui.runtime.internal.text.isAsciiLetterOrDigit
import top.wcpe.allincore.ui.runtime.internal.text.isWhitespaceCodePoint
import top.wcpe.allincore.ui.runtime.internal.text.offsetByCodePointsCompat

/**
 * 文本编辑核心：纯逻辑的「字符串 + 光标 + 选区 + 撤销栈」模型，无渲染/平台/反应式依赖。
 *
 * 采用 Swing JTextComponent 的 dot/mark 模型：[caret] 为活动端（光标/选区移动端），
 * [anchor] 为固定端。约定：选区为空 ⇔ caret == anchor；选区区间为 [min(caret,anchor), max(caret,anchor)]。
 *
 * 不变量：0 <= caret <= len、0 <= anchor <= len，且光标/选区端点永不落在代理对中间。
 *
 * 撤销栈采用「合并式撤销单元」：连续同类输入合并为一个撤销单元，
 * 栈容量上限 [UNDO_LIMIT]，超限丢最旧防内存无界增长。
 *
 * 职责边界：本类只负责文本/选区/撤销演化。[setText] 会清空撤销栈。
 */
class TextEditCore {

    enum class EditKind { TYPING, DELETE_BACKWARD, DELETE_FORWARD, STRUCTURAL }

    private data class Snapshot(val text: String, val caret: Int, val anchor: Int)

    // ===== 状态（backing 字段用 Value 后缀避 Java ABI 冲突）=====
    private var textValue: String = ""
    private var caretValue: Int = 0
    private var anchorValue: Int = 0

    private val undoStack: ArrayDeque<Snapshot> = ArrayDeque()
    private val redoStack: ArrayDeque<Snapshot> = ArrayDeque()
    private var lastEditKind: EditKind? = null

    // ===== 查询（方法式 getter 保留 Java `.text()`/`.caret()`/`.anchor()` ABI）=====
    fun text(): String = textValue
    fun caret(): Int = caretValue
    fun anchor(): Int = anchorValue

    fun hasSelection(): Boolean = caretValue != anchorValue
    fun selectionStart(): Int = minOf(caretValue, anchorValue)
    fun selectionEnd(): Int = maxOf(caretValue, anchorValue)
    fun selectedText(): String = textValue.substring(selectionStart(), selectionEnd())

    // ===== 外部赋值 =====

    fun setText(s: String?) {
        textValue = s ?: ""
        caretValue = textValue.length
        anchorValue = caretValue
        clearHistory()
    }

    // ===== 光标/选区操作 =====

    fun setCaret(pos: Int, extend: Boolean) {
        sealUndoUnit()
        val clamped = clampToBoundary(pos)
        caretValue = clamped
        if (!extend) {
            anchorValue = clamped
        }
    }

    fun moveCaret(delta: Int, extend: Boolean) {
        val target = offsetByCodePointsSafe(caretValue, delta)
        setCaret(target, extend)
    }

    fun moveToStart(extend: Boolean) {
        setCaret(0, extend)
    }
    fun moveToEnd(extend: Boolean) {
        setCaret(textValue.length, extend)
    }

    fun selectAll() {
        sealUndoUnit()
        anchorValue = 0
        caretValue = textValue.length
    }

    fun selectWordAt(pos: Int) {
        sealUndoUnit()
        val len = textValue.length
        var p = clampToBoundary(pos)
        if (len == 0) {
            anchorValue = 0
            caretValue = 0
            return
        }
        if (p >= len) {
            p = offsetByCodePointsSafe(len, -1)
        }
        val cp = textValue.codePointAtCompat(p)
        if (isWordBoundary(cp)) {
            anchorValue = p
            caretValue = p + charCountOf(cp)
            return
        }
        anchorValue = wordStart(p)
        caretValue = wordEnd(p)
    }

    // ===== 编辑操作 =====

    fun replaceSelection(s: String?) {
        replaceSelection(s, EditKind.STRUCTURAL)
    }
    fun replaceSelection(s: String?, kind: EditKind) {
        val insert = s ?: ""
        recordBeforeEdit(kind)
        applyReplaceSelection(insert)
    }

    fun typeText(s: String?) {
        replaceSelection(s, EditKind.TYPING)
    }
    fun pasteText(s: String?) {
        replaceSelection(s, EditKind.STRUCTURAL)
    }

    fun deleteBackward() {
        if (hasSelection()) {
            recordBeforeEdit(EditKind.STRUCTURAL)
            applyReplaceSelection("")
            return
        }
        if (caretValue <= 0) return
        recordBeforeEdit(EditKind.DELETE_BACKWARD)
        val prev = offsetByCodePointsSafe(caretValue, -1)
        textValue = textValue.substring(0, prev) + textValue.substring(caretValue)
        caretValue = prev
        anchorValue = prev
    }

    fun deleteForward() {
        if (hasSelection()) {
            recordBeforeEdit(EditKind.STRUCTURAL)
            applyReplaceSelection("")
            return
        }
        if (caretValue >= textValue.length) return
        recordBeforeEdit(EditKind.DELETE_FORWARD)
        val next = offsetByCodePointsSafe(caretValue, 1)
        textValue = textValue.substring(0, caretValue) + textValue.substring(next)
        anchorValue = caretValue
    }

    // ===== 撤销/重做 =====

    fun undo(): Boolean {
        if (undoStack.isEmpty()) return false
        redoStack.addLast(snapshot())
        restore(undoStack.removeLast())
        lastEditKind = null
        return true
    }

    fun redo(): Boolean {
        if (redoStack.isEmpty()) return false
        undoStack.addLast(snapshot())
        restore(redoStack.removeLast())
        lastEditKind = null
        return true
    }

    fun sealUndoUnit() {
        lastEditKind = null
    }

    // ===== 私有 =====

    private fun recordBeforeEdit(kind: EditKind) {
        val mergeable = kind != EditKind.STRUCTURAL
        val merge = mergeable && kind == lastEditKind
        if (!merge) pushUndoSnapshot(snapshot())
        redoStack.clear()
        lastEditKind = if (mergeable) kind else null
    }

    private fun pushUndoSnapshot(snap: Snapshot) {
        undoStack.addLast(snap)
        while (undoStack.size > UNDO_LIMIT) undoStack.removeFirst()
    }

    private fun snapshot() = Snapshot(textValue, caretValue, anchorValue)

    private fun restore(snap: Snapshot) {
        textValue = snap.text
        caretValue = snap.caret
        anchorValue = snap.anchor
    }

    private fun clearHistory() {
        undoStack.clear()
        redoStack.clear()
        lastEditKind = null
    }

    private fun applyReplaceSelection(insert: String) {
        val start = selectionStart()
        val end = selectionEnd()
        textValue = textValue.substring(0, start) + insert + textValue.substring(end)
        caretValue = start + insert.length
        anchorValue = caretValue
    }

    private fun clampToBoundary(pos: Int): Int {
        val len = textValue.length
        var p = pos.coerceIn(0, len)
        if (isSurrogatePair(textValue, p)) p--
        return p
    }

    private fun offsetByCodePointsSafe(from: Int, delta: Int): Int {
        val len = textValue.length
        val base = from.coerceIn(0, len)
        if (delta == 0) return base
        var cpIndex = textValue.codePointCountCompat(0, base) + delta
        val total = textValue.codePointCountCompat(0, len)
        if (cpIndex < 0) cpIndex = 0
        if (cpIndex > total) cpIndex = total
        return textValue.offsetByCodePointsCompat(0, cpIndex)
    }

    // ===== 词边界导航（实例方法，访问 textValue）=====

    private fun wordStart(p: Int): Int {
        var s = p
        while (s > 0) {
            val before = offsetByCodePointsSafe(s, -1)
            if (isWordBoundary(textValue.codePointAtCompat(before))) break
            s = before
        }
        return s
    }

    private fun wordEnd(p: Int): Int {
        var e = p
        val len = textValue.length
        while (e < len) {
            if (isWordBoundary(textValue.codePointAtCompat(e))) break
            e = offsetByCodePointsSafe(e, 1)
        }
        return e
    }

    companion object {
        const val UNDO_LIMIT: Int = 200

        private fun isWordBoundary(cp: Int): Boolean {
            if (isWhitespaceCodePoint(cp)) return true
            return cp < 0x80 && !isAsciiLetterOrDigit(cp) && cp != '_'.code
        }

        private fun isSurrogatePair(text: String, p: Int): Boolean {
            if (p <= 0 || p >= text.length) return false
            return text[p].isLowSurrogate() &&
                text[p - 1].isHighSurrogate()
        }
    }
}
